import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { useMediaQuery, useTheme } from "@mui/material";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useMemberData from "layouts/tables/data/memberReportTabledata";

function Member() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchMemberData,
    handleExport,
  } = useMemberData();

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "phone"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchMemberData(startDate, endDate);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading Members...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      Church Member Report - {formatDisplayDate(dateRange.startDate)}
                      {!isSameDay(dateRange.startDate, dateRange.endDate) &&
                        ` to ${formatDisplayDate(dateRange.endDate)}`}
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        size="small"
                        value={dateRangeType}
                        onChange={handleDateRangeTypeChange}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: 1,
                          width: 120,
                        }}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                        <MenuItem value="custom">Custom Range</MenuItem>
                      </TextField>

                      {dateRangeType === "custom" && (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="From Date"
                            value={dateRange.startDate}
                            onChange={handleStartDateChange}
                            maxDate={dateRange.endDate}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  backgroundColor: "white",
                                  borderRadius: 1,
                                  width: 150,
                                }}
                              />
                            )}
                          />
                          <DatePicker
                            label="To Date"
                            value={dateRange.endDate}
                            onChange={handleEndDateChange}
                            minDate={dateRange.startDate}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  backgroundColor: "white",
                                  borderRadius: 1,
                                  width: 150,
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )}

                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={handleTodayClick}
                        sx={{ minWidth: "100px" }}
                      >
                        Today
                      </MDButton>

                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={() => handleExport("csv")}
                        startIcon={<Icon>download</Icon>}
                      >
                        CSV
                      </MDButton>

                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={() => handleExport("pdf")}
                        startIcon={<Icon>picture_as_pdf</Icon>}
                      >
                        PDF
                      </MDButton>

                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={() => handleExport("print")}
                        startIcon={<Icon>print</Icon>}
                      >
                        Print
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns,
                    rows: rows.map((row) => ({
                      ...row,
                      // Ensure all data is included even if columns are hidden
                      originalData: row.originalData || {
                        id: row.id,
                        memberName: row.memberName,
                        phone: row.phone,
                        email: row.email,
                        department: row.department,
                        ministry: row.ministry,
                      },
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search Members..."
                  printFullTable // Ensure all columns print regardless of screen size
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Member;
